import type { PortableTextBlock } from './sanity-types';

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/**
 * Read one swept field back out of its family array.
 *
 * ADR-0068 decision 8 collapsed same-shaped fields into shared arrays keyed by `role`: what was
 * `HeroCard.ribbonContent` is an entry of `HeroCard.richText` with `role === 'ribbonContent'`, and
 * `GenericFormInputField.genericInputField` an entry of its `genericFields`. 1,860 field entries
 * across 10 arrays moved this way, so components cannot read them as plain properties.
 *
 * Returns the whole entry, minus its bookkeeping keys — for a compound field (a form field's
 * `{ name, label, placeholder, … }`) that object *is* the value. Use {@link richTextByRole} for the
 * Rich Text family, whose entries wrap their value in a single `content` member.
 *
 * Entries carry no `_type`, so this cannot infer which family an entry belongs to; the caller names
 * the array, which it knows from the schema. That is why there is no general "un-sweep everything"
 * pass on the adapter.
 */
export const familyEntryByRole = (entries: unknown, role: string): Record<string, unknown> | undefined => {
  if (!Array.isArray(entries)) return undefined;
  const match = entries.find(entry => isObject(entry) && entry.role === role);
  if (!isObject(match)) return undefined;

  const { role: _role, _key, _type, ...value } = match;
  return value;
};

/**
 * Read one swept Rich Text field back out of its family array, unwrapping the entry's single
 * `content` member so callers get the block array the field originally held.
 */
export const richTextByRole = (entries: unknown, role: string): PortableTextBlock[] | undefined => {
  const entry = familyEntryByRole(entries, role);
  const content = entry?.content;
  return Array.isArray(content) && content.length > 0 ? (content as PortableTextBlock[]) : undefined;
};
